// ============================================================
// SkyPlus HasFeature Directive
// app/components/skyplus/shared/sector/has-feature.directive.ts
//
// Structural directive to gate template content by feature flag.
// Equivalent to *ngIf="sector.feature('x')" but more readable
// and colocated with the sector module.
//
// USAGE:
//   <div *spHasFeature="'aiChat'">
//     <button>Ask your documents</button>
//   </div>
//
// When the feature is off, the element is not rendered at all -
// not hidden with display:none, not in the DOM, not in the Angular
// component tree. Same behaviour as *ngIf with a false condition.
//
// This directive is the canonical way to gate any UI element in
// an existing component. See the recipe in tenant-kit/docs/
// ADDING-A-FEATURE.md for the full workflow.
// ============================================================

import {
  Directive,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { SectorService } from './sector.service';
import { FeatureFlags } from './sector.model';

@Directive({
  selector: '[spHasFeature]'
})
export class HasFeatureDirective implements OnInit, OnDestroy {

  private flag: keyof FeatureFlags | null = null;
  private sub: Subscription | null = null;
  private rendered = false;

  @Input() set spHasFeature(value: keyof FeatureFlags) {
    this.flag = value;
    this.update();
  }

  constructor(
    private template: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private sector: SectorService
  ) {}

  ngOnInit(): void {
    this.sub = this.sector.features$.subscribe(() => this.update());
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  private update(): void {
    if (!this.flag) return;
    const enabled = this.sector.feature(this.flag);

    if (enabled && !this.rendered) {
      this.viewContainer.createEmbeddedView(this.template);
      this.rendered = true;
    } else if (!enabled && this.rendered) {
      this.viewContainer.clear();
      this.rendered = false;
    }
  }
}
